import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { useDialog } from '@/_shared/hooks/useDialog';
import { getEmployeesFiltersCall } from '../../state/api';

type Props = {
  setFilters: Dispatch<
    SetStateAction<
      | {
          q: string;
          departments?: string[];
          roles?: string[];
        }
      | undefined
    >
  >;
  filters?: { q?: string; departments?: string[]; roles?: string[] };
};

export const FiltersEmployees: FC<Props> = ({ setFilters, filters }) => {
  const t = useTranslations('people.filters');
  const { closeDialog: closeDialogFilters } = useDialog('filters');
  const { execute: executeGetEmployeesFilters, response: responseGetEmployeesFilters } =
    getEmployeesFiltersCall.hook();

  const {
    handleSubmit,
    formState: { isValid },
    control,
    setValue,
    watch,
  } = useForm();

  const fieldValues = watch();

  const roleItems = useMemo(
    () =>
      responseGetEmployeesFilters?.roles.map(role => {
        return { value: role.key, label: role.value };
      }),
    [responseGetEmployeesFilters],
  );

  const departmentsItems = useMemo(
    () =>
      responseGetEmployeesFilters?.departments.map(department => ({
        value: department.key,
        label:
          department.key?.toLowerCase() === 'unknown' ? t('unknownDepartment') : department.value,
      })),
    [responseGetEmployeesFilters, t],
  );

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    data => {
      const departments = Array.isArray(data.departments)
        ? data.departments.map((d: { value: string }) => d.value)
        : undefined;
      const roles = Array.isArray(data.roles)
        ? data.roles.map((d: { value: string }) => d.value)
        : undefined;
      if (isValid && setFilters) {
        setFilters(prev => ({
          ...prev,
          q: filters?.q || '',
          departments,
          roles,
        }));
        closeDialogFilters();
      }
    },
    [closeDialogFilters, filters?.q, isValid, setFilters],
  );

  const onResetDepartments = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      departments: undefined,
      q: filters?.q || '',
    }));
    setValue('departments', []);
    closeDialogFilters();
  }, [closeDialogFilters, filters?.q, setFilters, setValue]);

  const onResetRoles = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      roles: undefined,
      q: filters?.q || '',
    }));
    setValue('roles', []);
    closeDialogFilters();
  }, [closeDialogFilters, filters?.q, setFilters, setValue]);

  useEffect(() => {
    executeGetEmployeesFilters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box display='flex' flexDirection='column'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <Box display='flex' justifyContent='space-between' alignItems='center' mb='xxs'>
              <Typography variant='p2/light'>{t('fields.departments')}</Typography>
              {fieldValues?.departments?.length > 0 && (
                <Typography
                  variant='p2/light'
                  color='white'
                  style={{ cursor: 'pointer' }}
                  onClick={onResetDepartments}
                >
                  {t('cta.reset')}
                </Typography>
              )}
            </Box>
            <Box
              opacity={fieldValues?.roles?.length > 0 ? 0.5 : 1}
              disabled={fieldValues?.roles?.length > 0}
              style={{
                pointerEvents: fieldValues?.roles?.length > 0 ? 'none' : 'auto',
              }}
            >
              <Form.FormDropdownMultiple
                zIndex={300}
                control={control}
                name='departments'
                items={departmentsItems || []}
                placeholder={filters?.departments ? filters.departments.join(', ') : 'Departments'}
              />
            </Box>
          </Box>
          <Box mt='s'>
            <Box display='flex' justifyContent='space-between' alignItems='center' mb='xxs'>
              <Typography variant='p2/light'>{t('fields.roles')}</Typography>
              {fieldValues?.roles?.length > 0 && (
                <Typography
                  variant='p2/light'
                  color='white'
                  style={{ cursor: 'pointer' }}
                  onClick={onResetRoles}
                >
                  {t('cta.reset')}
                </Typography>
              )}
            </Box>
            <Box
              opacity={fieldValues?.departments?.length > 0 ? 0.5 : 1}
              disabled={fieldValues?.departments?.length > 0}
              style={{
                pointerEvents: fieldValues?.departments?.length > 0 ? 'none' : 'auto',
              }}
            >
              <Form.FormDropdownMultiple
                zIndex={300}
                control={control}
                name='roles'
                items={roleItems || []}
                placeholder={filters?.roles ? filters.roles.join(', ') : 'Roles'}
              />
            </Box>
          </Box>
          <Box justifyContent='flex-end' display='flex' flexDirection='row'>
            <Form.Buttons.Cta onClick={closeDialogFilters} mt='l' mr='xs'>
              {t('cta.cancel')}
            </Form.Buttons.Cta>
            <Form.Buttons.Submit mt='l' data-testid='filter-button-search'>
              {t('cta.apply')}
            </Form.Buttons.Submit>
          </Box>
        </form>
      </Box>
    </Box>
  );
};
